/* eslint-disable no-console */
/* eslint-disable no-shadow */
import React, {
  FC,
  useState,
  useCallback,
  useLayoutEffect,
  useEffect,
} from 'react'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { useAnalytics } from 'reactfire'

import { useDispatch, iRootState } from '../../store'
import { City } from '../../store/models/user'
import { ProgressBar } from '../../components/ProgressBar'
import { LoadingScreen } from '../../components/Loading'
import { CitySelect } from '../../components/CitySelect'
import * as routes from '../../constants/routes'
import Header from '../../components/Header/Header'
import { sendInitialQuizAnswers } from '../LogIn/Login/utils'

import ProfileSet from './ProfileSet'
import AboutMe from './AboutMe'
import Photo from './Photo'
import ToolBar from './ToolBar'
import {
  Wrap,
  StyledForm,
  Title,
  Paragraph,
  FormWrap,
  InnerWrap,
} from './styled'

import { updateUser, checkUsername } from 'src/api/user/user'
import { analyticsEvents } from 'src/constants/analytics/events'
import { useDebounce } from 'src/hooks/useDebounce'

export enum SignUpStep {
  'PROFILE_SET' = 'PROFILE_SET',
  'ABOUT_ME' = 'ABOUT_ME',
  'PHOTO' = 'PHOTO',
}

const MAX_AGE = 13
const maxDate = new Date(
  new Date().setFullYear(new Date().getFullYear() - MAX_AGE)
).toISOString()

const stateSelector = (state: iRootState) => ({
  profilePicture: state.user.data?.profilePicture,
  firstName: state.user.data?.firstName,
  birthday: state.user.data?.birthday,
  username: state.user.data?.username,
  id: state.user.data?.id,
  city: state.user.data?.city,
  instagram: state.user.data?.instagram,
  twitter: state.user.data?.twitter,
  profileComplete: state.user.data?.profileComplete,
  isAuthResolved: state.user.isAuthResolved,
})

const SignUpView: FC = () => {
  const dispatch = useDispatch()
  const { push } = useHistory()
  const { logEvent } = useAnalytics()
  const {
    id,
    username,
    firstName,
    birthday,
    city,
    instagram,
    twitter,
    profilePicture,
    profileComplete,
    isAuthResolved,
  } = useSelector(stateSelector)

  const [isLoading, setIsLoading] = useState(false)
  const [selectedGender, setSelectedGender] = useState<{
    id: number
    name: string
  }>()
  const [disabledForm, setDisabledForm] = useState(true)
  const [step, setStep] = useState(SignUpStep.PROFILE_SET)
  const [openCity, setOpenCity] = useState(false)
  const [selectedCity, setSelectedCity] = useState<City | undefined>()
  const [usernameTaken, setUsernameTaken] = useState<boolean>()
  const [usernameCheckError, setUsernameCheckError] = useState<boolean>()

  const formik01 = useFormik({
    validateOnMount: true,
    initialValues: {
      birthday: '',
    },
    validationSchema: Yup.object({
      birthday: Yup.date()
        .required('Required')
        .max(maxDate, 'You must be at least 13 years old.'),
    }),
    onSubmit: (values) => {
      dispatch.user.updateUser({
        birthday: values.birthday,
        gender: selectedGender,
      })
      logEvent(analyticsEvents.onboardingProfileCompleted)
    },
  })

  const formik02 = useFormik({
    validateOnMount: true,
    initialValues: {
      username: '',
      firstName: '',
      instagram: '',
      twitter: '',
    },
    validationSchema: Yup.object({
      username: Yup.string()
        .required('Required')
        .matches(/^[A-Za-z0-9_@\-"',\s]+$/u, 'Invalid character')
        .min(2, 'Username must be at least 2 characters long.')
        .max(30, 'Too long username'),
      firstName: Yup.string()
        .required('Required')
        .matches(/^[A-Za-z0-9_@\-"',\s]+$/u, 'Invalid character')
        .min(2, 'Your name must be at least 2 characters long.')
        .max(30, 'Too long name'),
      instagram: Yup.string(),
      twitter: Yup.string(),
    }),
    onSubmit: (values) => {
      dispatch.user.updateUser({
        username: values.username,
        firstName: values.firstName,
        instagram: values.instagram,
        twitter: values.twitter,
      })
      logEvent(analyticsEvents.onboardingAboutMeCompleted)
    },
  })

  const debouncedUsername = useDebounce(formik02.values.username, 500)

  const postUser = useCallback(async () => {
    const cityData = city?.state
      ? {
          name: city?.name || '',
          country: city?.country || '',
          state: city?.state || '',
        }
      : {
          name: city?.name || '',
          country: city?.country || '',
        }

    try {
      const updatedUser = await updateUser({
        username,
        firstName,
        birthday,
        genderId: selectedGender?.id,
        city: cityData,
        instagram,
        twitter,
        profilePicture,
      })

      dispatch.user.setUser(updatedUser)
      await sendInitialQuizAnswers()
    } catch (error) {
      console.log(error)
    }
  }, [
    username,
    firstName,
    birthday,
    selectedGender,
    city,
    instagram,
    twitter,
    profilePicture,
    dispatch.user,
  ])

  const handleClick = useCallback(async () => {
    if (step === SignUpStep.PROFILE_SET) {
      formik01.handleSubmit()
      setDisabledForm(true)
      setStep(SignUpStep.ABOUT_ME)
    }
    if (step === SignUpStep.ABOUT_ME) {
      formik02.handleSubmit()
      setIsLoading(false)
      setDisabledForm(true)
      setStep(SignUpStep.PHOTO)
    }
    if (step === SignUpStep.PHOTO) {
      setIsLoading(true)
      await postUser()
      setIsLoading(false)
      push(routes.PROFILE)
    }
  }, [step, formik01, formik02, postUser, push])

  useLayoutEffect(() => {
    if (step === SignUpStep.PROFILE_SET) {
      formik01.isValid && selectedGender
        ? setDisabledForm(false)
        : setDisabledForm(true)
    }
    if (step === SignUpStep.ABOUT_ME) {
      formik02.isValid && selectedCity && !usernameTaken && !usernameCheckError
        ? setDisabledForm(false)
        : setDisabledForm(true)
    }
    if (step === SignUpStep.PHOTO) {
      profilePicture && profilePicture.length > 1
        ? setDisabledForm(false)
        : setDisabledForm(true)
    }
  }, [
    formik01.isValid,
    formik02.isValid,
    selectedCity,
    selectedGender,
    profilePicture,
    step,
    setDisabledForm,
    usernameTaken,
    usernameCheckError,
  ])

  useEffect(() => {
    const isUserLoggedIn = isAuthResolved && !id
    if (isUserLoggedIn || profileComplete) {
      push(routes.LANDING)
    }
  }, [id, isAuthResolved, profileComplete, push])

  useEffect(() => {
    const debouncedFetch = async () => {
      if (debouncedUsername.length > 0) {
        try {
          setUsernameTaken(false)
          setUsernameCheckError(false)
          const resp = await checkUsername(debouncedUsername)
          setUsernameTaken(resp.usernameTaken)
        } catch (error) {
          setUsernameCheckError(true)
        }
      } else {
        setUsernameTaken(false)
      }
    }

    debouncedFetch()
  }, [debouncedUsername])

  return (
    <Wrap>
      <Header />
      <InnerWrap>
        {isLoading && <LoadingScreen />}
        <CitySelect
          openCity={openCity}
          setOpenCity={setOpenCity}
          setSelectedCity={setSelectedCity}
        />
        {step === SignUpStep.PROFILE_SET && (
          <FormWrap>
            <ProgressBar progress={30} />
            <Title>Profile</Title>
            <Paragraph>Say something about you</Paragraph>
            <StyledForm onSubmit={formik01.handleSubmit}>
              <ProfileSet
                setGender={setSelectedGender}
                selectedGender={selectedGender}
                onChange={formik01.handleChange}
                value={formik01.values.birthday}
                errors={formik01.errors}
                touched={formik01.touched}
                onBlur={formik01.handleBlur}
              />
            </StyledForm>
          </FormWrap>
        )}
        {step === SignUpStep.ABOUT_ME && (
          <FormWrap>
            <ProgressBar progress={60} />
            <Title>About me</Title>
            <Paragraph>Say something about you</Paragraph>
            <StyledForm onSubmit={formik02.handleSubmit}>
              <AboutMe
                onChange={formik02.handleChange}
                values={formik02.values}
                setOpenCity={setOpenCity}
                selectedCity={selectedCity}
                errors={formik02.errors}
                touched={formik02.touched}
                onBlur={formik02.handleBlur}
                usernameTaken={usernameTaken}
                usernameCheckError={usernameCheckError}
              />
            </StyledForm>
          </FormWrap>
        )}
        {step === SignUpStep.PHOTO && (
          <FormWrap>
            <ProgressBar progress={100} />
            <Title>Profile photo</Title>
            <Paragraph>Upload your profile photo</Paragraph>
            <Photo setIsLoading={setIsLoading} uid={id || ''} />
          </FormWrap>
        )}
      </InnerWrap>
      <ToolBar
        disabledButton={disabledForm}
        onClick={handleClick}
        step={step}
        setStep={setStep}
      />
    </Wrap>
  )
}

export default SignUpView
